import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase.js';
import Swal from 'sweetalert2';
import { Trash2, Plus, ExternalLink, Save, LayoutGrid, Link as LinkIcon, Tag, Edit, X, Upload, Loader2 } from 'lucide-react';
import '../../styles/Reportes.css'; // Reutilizamos estilos de tablas existentes

const CATEGORIES = ['Vehículos', 'Vivienda', 'Materiales', 'Servicios', 'Otros'];

const GestionBeneficios = () => {
    const [benefits, setBenefits] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // --- Form State ---
    const [editingId, setEditingId] = useState(null);
    const [title, setTitle] = useState('');
    const [url, setUrl] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('Vehículos');
    const [imageUrl, setImageUrl] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);

    // Helper para llamar a la API
    const callApi = (fnName, data = {}) => {
        const callable = httpsCallable(functions, 'api');
        return callable({ __fn: fnName, ...data });
    };

    const fetchBenefits = async () => {
        setLoading(true);
        try {
            const response = await callApi('getBenefits');
            if (response.data.success) {
                setBenefits(response.data.benefits);
            }
        } catch (error) {
            console.error("Error cargando beneficios:", error);
            // No mostramos alerta aquí para no ser intrusivos al cargar
        } finally {
            setLoading(false);
        }
    };

    // Cargar beneficios al montar
    useEffect(() => {
        fetchBenefits();
    }, [fetchBenefits]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            Swal.fire('Error', 'La imagen es demasiado grande (máx 5MB).', 'error');
            return;
        }

        setUploadingImage(true);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = async () => {
            try {
                const base64data = reader.result;
                const fileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
                const path = `benefits/${Date.now()}_${fileName}`;
                
                const response = await callApi('uploadFile', { file: base64data, path });
                const downloadURL = response.data?.data?.downloadURL;
                
                if (downloadURL) {
                     setImageUrl(downloadURL);
                } else {
                    throw new Error('No se recibió la URL.');
                }
            } catch (error) {
                console.error("Error uploading image:", error);
                Swal.fire('Error', 'No se pudo subir la imagen.', 'error');
            } finally {
                setUploadingImage(false);
                e.target.value = null;
            }
        };
    };

    const resetForm = () => {
        setEditingId(null);
        setTitle('');
        setUrl('');
        setDescription('');
        setCategory('Vehículos');
        setImageUrl('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title || !url || !description) {
            Swal.fire('Campos incompletos', 'Por favor completa título, URL y descripción.', 'warning');
            return;
        }

        setIsSubmitting(true);

        const benefitData = {
            title,
            url,
            description,
            category,
            imageUrl,
        };

        try {
            await callApi('manageBenefit', {
                action: editingId ? 'update' : 'create',
                id: editingId,
                benefitData
            });

            Swal.fire('¡Guardado!', `El beneficio ha sido ${editingId ? 'actualizado' : 'publicado'}.`, 'success');
            resetForm();
            fetchBenefits(); // Recargar lista
        } catch (error) {
            console.error("Error guardando beneficio:", error);
            Swal.fire('Error', `No se pudo guardar el beneficio: ${error.message}`, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (benefit) => {
        setEditingId(benefit.id);
        setTitle(benefit.title);
        setUrl(benefit.url);
        setDescription(benefit.description);
        setCategory(benefit.category);
        setImageUrl(benefit.imageUrl || '');
        // Scroll to top to see the form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Eliminar beneficio?',
            text: "Esta acción no se puede deshacer.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar'
        });

        if (result.isConfirmed) {
            try {
                await callApi('manageBenefit', { action: 'delete', id });
                fetchBenefits(); // Recargar lista
                Swal.fire('Eliminado', 'El beneficio ha sido eliminado.', 'success');
            } catch (error) {
                Swal.fire('Error', 'No se pudo eliminar.', 'error');
            }
        }
    };

    return (
        <div className="main-panel">
            <h2 className="section-title">Gestión de Beneficios y Aliados</h2>
            <p>Administra los enlaces a bienes (vehículos, viviendas, materiales) visibles para los usuarios.</p>
            <style>{`
                .spinner { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>

            {/* Formulario de Creación */}
            <div className="admin-edit-card" style={{ marginTop: '20px', marginBottom: '30px' }}>
                <h3 className="section-subtitle">
                    {editingId ? <Edit size={18} /> : <Plus size={18} />}
                    {editingId ? ' Editar Beneficio' : ' Agregar Nuevo Beneficio'}
                </h3>
                <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                    
                    <div className="input-group">
                        <label>Título del Bien / Aliado</label>
                        <input 
                            type="text" 
                            placeholder="Ej: Toyota - Flota Comercial" 
                            value={title} 
                            onChange={e => setTitle(e.target.value)} 
                            required 
                        />
                    </div>

                    <div className="input-group">
                        <label>Categoría</label>
                        <div style={{ position: 'relative' }}>
                            <Tag size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#666' }} />
                            <select 
                                value={category} 
                                onChange={e => setCategory(e.target.value)}
                                style={{ paddingLeft: '35px', width: '100%' }}
                            >
                                {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="input-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Enlace (URL de destino)</label>
                        <div style={{ position: 'relative' }}>
                            <LinkIcon size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#666' }} />
                            <input 
                                type="url" 
                                placeholder="https://..." 
                                value={url} 
                                onChange={e => setUrl(e.target.value)} 
                                required 
                                style={{ paddingLeft: '35px' }}
                            />
                        </div>
                    </div>

                    <div className="input-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Descripción Corta</label>
                        <textarea 
                            placeholder="Describe el beneficio para el usuario..." 
                            value={description} 
                            onChange={e => setDescription(e.target.value)} 
                            rows="2"
                            required
                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                        />
                    </div>

                    <div className="input-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Imagen (URL o Subir Archivo)</label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <div style={{ position: 'relative', flexGrow: 1 }}>
                                <LinkIcon size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#666' }} />
                                <input 
                                    type="url" 
                                    placeholder="https://..." 
                                    value={imageUrl} 
                                    onChange={e => setImageUrl(e.target.value)} 
                                    style={{ paddingLeft: '35px', width: '100%' }}
                                />
                            </div>
                            <label className="action-btn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', margin: 0, height: '42px', whiteSpace: 'nowrap' }}>
                                {uploadingImage ? <Loader2 size={16} className="spinner" /> : <Upload size={16} />}
                                <span>{uploadingImage ? 'Subiendo...' : 'Subir Foto'}</span>
                                <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploadingImage} />
                            </label>
                        </div>
                        {imageUrl && (
                            <div style={{ marginTop: '10px', border: '1px solid #eee', padding: '5px', borderRadius: '8px', display: 'inline-block' }}>
                                <img src={imageUrl} alt="Vista previa" style={{ height: '100px', borderRadius: '4px', objectFit: 'cover', display: 'block' }} />
                            </div>
                        )}
                    </div>

                    <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <button 
                            type="submit" 
                            className="login-button" 
                            disabled={isSubmitting}
                            style={{ flexGrow: 1 }}
                        >
                            {isSubmitting ? 'Guardando...' : (
                                editingId 
                                ? <><Save size={18} style={{ marginRight: '8px' }} /> Actualizar Beneficio</> 
                                : <><Plus size={18} style={{ marginRight: '8px' }} /> Publicar Beneficio</>
                            )}
                        </button>
                        {editingId && (
                            <button 
                                type="button" 
                                className="action-btn"
                                onClick={resetForm}
                                disabled={isSubmitting}
                            >
                                <X size={18} style={{ marginRight: '8px' }} /> Cancelar
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* Lista de Beneficios */}
            <div className="report-table-container">
                <table className="report-table">
                    <thead>
                        <tr>
                            <th>Imagen</th>
                            <th>Título / Descripción</th>
                            <th>Categoría</th>
                            <th>Enlace</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" className="empty-state-text">Cargando...</td></tr>
                        ) : benefits.length === 0 ? (
                            <tr><td colSpan="5" className="empty-state-text">No hay beneficios registrados.</td></tr>
                        ) : (
                            benefits.map(benefit => (
                                <tr key={benefit.id}>
                                    <td>
                                        {benefit.imageUrl ? (
                                            <img src={benefit.imageUrl} alt="icon" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                                        ) : (
                                            <LayoutGrid size={24} color="#ccc" />
                                        )}
                                    </td>
                                    <td>
                                        <strong>{benefit.title}</strong>
                                        <div style={{ fontSize: '0.85rem', color: '#666' }}>{benefit.description}</div>
                                    </td>
                                    <td>
                                        <span className="status-badge" style={{ background: '#e0f2fe', color: '#0369a1' }}>
                                            {benefit.category}
                                        </span>
                                    </td>
                                    <td>
                                        <a href={benefit.url} target="_blank" rel="noopener noreferrer" className="link-button" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            Visitar <ExternalLink size={14} />
                                        </a>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button 
                                                onClick={() => handleEdit(benefit)} 
                                                className="action-btn"
                                                title="Editar"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(benefit.id)} 
                                                className="action-btn reject"
                                                title="Eliminar"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default GestionBeneficios;